import React, { useState } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  LocalPharmacy,
  LocalPharmacy as LocalPharmacyIcon,
  LocalPharmacySharp,
  People as PeopleIcon,
} from "@mui/icons-material";
import "animate.css";

const translations = {
  en: {
    welcome: "Welcome to Said Ahmed Memorial Hospital",
    quality_healthcare: "Providing Quality Healthcare with Compassion",
    trusted_partner: "Your trusted partner in health, committed to excellence and care",
    receptionist: "Receptionist",
    pharmacy_dispenser: "Dispenser",
    human_resources: "HR",
    account: "Account",
    pharmacy: "Pharmacy",
  },
  ar: {
    welcome: "مرحبًا بكم في مستشفى سعيد أحمد التذكاري",
    quality_healthcare: "تقديم الرعاية الصحية عالية الجودة مع التعاطف",
    trusted_partner: "شريكك الموثوق في الصحة، ملتزمون بالتميز والرعاية",
    receptionist: "استقبال",
    pharmacy_dispenser: "موزع",
    human_resources: "الموارد البشرية",
    account: "الحساب",
    pharmacy: "الصيدلية",
  },
  ur: {
    welcome: "سید احمد میموریل ہسپتال میں خوش آمدید",
    quality_healthcare: "معیاری صحت کی دیکھ بھال فراہم کرنا، ہمدردی کے ساتھ",
    trusted_partner: "آپ کا صحت میں اعتماد کا پارٹنر، عمدگی اور دیکھ بھال کے ساتھ وابستہ",
    receptionist: "استقبالیہ",
    pharmacy_dispenser: " ڈسپینسر",
    human_resources: "انسانی وسائل",
    account: "اکاؤنٹ",
    pharmacy: "فارمیسی",
  },
};

const ButtonCard = ({ button, onClick, currentLang }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-xl overflow-hidden transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:opacity-90">
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={onClick}
        className="relative w-full h-56 flex flex-col items-center justify-center text-lg font-semibold p-6 transition-all duration-300 hover:scale-105"
        aria-label={button.label}
      >
        <div className="flex flex-col items-center justify-center w-full h-full">
          <div className="mb-4 w-24 h-24 rounded-full bg-white flex justify-center items-center shadow-2xl">
            {React.cloneElement(button.icon, { sx: { color: "#2D3748", fontSize: 50 } })}
          </div>
          <div className={`w-full text-center ${button.bgColor} text-white text-xl font-bold py-3`}>
            {translations[currentLang][button.label]} 
          </div>
        </div>
      </Button>
    </div>
  );
};

const Home = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [currentLang, setCurrentLang] = useState("en"); 
  const navigate = useNavigate();

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };

  const handleButtonClick = (route) => {
    const newTab = window.open(route, '_blank', 'noopener,noreferrer');
    if (newTab) {
      newTab.focus();
    }
  };

  const changeLanguage = (lang) => {
    setCurrentLang(lang); 
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-blue-50 via-teal-50 to-green-50'} flex flex-col justify-between transition-all`}>
      <div className="flex justify-center space-x-4 p-4 bg-gray-800 text-white z-50 fixed w-full top-0 left-0">
        <Button
          variant="contained"
          color={currentLang === "en" ? "primary" : "default"}
          onClick={() => changeLanguage("en")}
          className="text-xl font-bold"
        >
          EN
        </Button>
        <Button
          variant="contained"
          color={currentLang === "ar" ? "primary" : "default"}
          onClick={() => changeLanguage("ar")}
          className="text-xl font-bold"
        >
          AR
        </Button>
        <Button
          variant="contained"
          color={currentLang === "ur" ? "primary" : "default"}
          onClick={() => changeLanguage("ur")}
          className="text-xl font-bold"
        >
          UR
        </Button>
      </div>

      <div className={`flex flex-col justify-center items-center ${isDarkMode ? 'bg-gradient-to-r from-teal-600 to-blue-800' : 'bg-gradient-to-r from-teal-400 to-blue-600'} text-white h-full px-4 py-16 sm:py-32 transition-all`}>
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 
            className={`text-5xl font-extrabold leading-tight mb-4 animate__animated animate__fadeIn animate__delay-1s`}
            style={{ fontFamily: 'Poppins, sans-serif', userSelect: 'none' }}
          >
            {translations[currentLang].welcome}
          </h1>
          <p className="text-lg font-medium mb-6 animate__animated animate__fadeIn animate__delay-2s" style={{ userSelect: 'none' }}>
            {translations[currentLang].quality_healthcare}
          </p>
          <p className="text-sm mb-8 animate__animated animate__fadeIn animate__delay-3s" style={{ userSelect: 'none' }}>
            {translations[currentLang].trusted_partner}
          </p>
        </div>
      </div>

      <div className="px-4 py-12 sm:py-16 flex-grow">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-16">
          {[         
  { route: "https://receptionist.samhospital.org", icon: <AccountCircleIcon />, bgColor: "bg-blue-500", label: "receptionist" },
  { route: "https://dispenser.samhospital.org", icon: <LocalPharmacyIcon />, bgColor: "bg-teal-400", label: "pharmacy_dispenser" },
  { route: "https://hr.samhospital.org", icon: <PeopleIcon />, bgColor: "bg-green-500", label: "human_resources" },
  { route: "https://accounts.samhospital.org", icon: <AdminPanelSettingsIcon />, bgColor: "bg-yellow-500", label: "account" },
  { route: "https://pharmacy.samhospital.org", icon: <LocalPharmacySharp />, bgColor: "bg-sky-950", label: "pharmacy" }
          ].map((button, index) => (
            <ButtonCard
              key={index}
              button={button}
              onClick={() => handleButtonClick(button.route)}
              currentLang={currentLang} // Pass current language
            />
          ))}
        </div>
      </div>

      <div className="bg-gray-800 text-white py-6 text-center">
        <p>&copy; 2024 Said Ahmed Memorial Hospital. All Rights Reserved.</p>
      </div>

      <div className="fixed bottom-8 right-8 z-50">
        <Button
          variant="contained"
          color={isDarkMode ? "secondary" : "primary"}
          onClick={toggleTheme}
          className={`rounded-full p-8 shadow-xl transition-all hover:scale-125 text-2xl ${isDarkMode ? 'font-bold' : 'font-semibold'}`}
        >
          {isDarkMode ? "🌞 Light Mode" : "🌙 Dark Mode"}
        </Button>
      </div>
    </div>
  );
};

export default Home;
